import * as React from "react";
import type { Item } from "../ReportServices";

/**
 * Parameter lists coming back from the server can hold six figures worth of items (jobs, for
 * example). Handing all of them to a Fluent Combobox makes opening the dropdown and every
 * keystroke unusably slow, because each item becomes a React element and a registered option.
 *
 * This hook keeps the full list intact for selection, filtering and "select all", but only hands
 * the combobox a prefix of it, growing that prefix as the user scrolls (or arrows) towards the end
 * of the rendered options.
 */

/** How many options are rendered when a dropdown is opened or the filter text changes. */
const INITIAL_VISIBLE_COUNT = 50;
/** How many more options are appended each time the user reaches the end of the rendered list. */
const LOAD_MORE_COUNT = 50;
/** Distance from the bottom of the listbox, in pixels, at which the next page is appended. */
const SCROLL_THRESHOLD_PX = 120;
/** How close the keyboard highlight may get to the last rendered option before the next page is appended. */
const KEYBOARD_LOOKAHEAD = 5;

const EMPTY_ITEMS: Item[] = [];

export type IncrementalOptions = {
  /** The items to render as options - a prefix of the filtered list. */
  visibleItems: Item[];
  /** The full filtered list (not paged) - the target set for a filter-scoped "select all". */
  filteredItems: Item[];
  /** Props to spread onto the combobox's `listbox` slot. */
  listboxProps: { onScroll: React.UIEventHandler };
  /** Grows the rendered list when the keyboard highlight nears its end. */
  onActiveOptionChange: (event: any, data: any) => void;
  /**
   * Fluent re-highlights the first option whenever the option children change and nothing is
   * highlighted, and highlighting scrolls that option into view - which would yank the list back
   * to the top every time a page is appended. Disabled only for lists large enough to be paged, so
   * short dropdowns keep their usual auto-highlight behaviour.
   */
  disableAutoFocus: boolean;
};

export const useIncrementalOptions = (
  items: Item[] | undefined,
  query: string,
  isOpen: boolean
): IncrementalOptions => {
  const filteredItems = React.useMemo(() => {
    const allItems = items ?? EMPTY_ITEMS;
    if (query == "") {
      return allItems;
    }
    // A comma-separated query matches an item if ANY of the terms match - "abc,xyz" is an OR, not
    // a stricter AND filter.
    const terms = query
      .split(",")
      .map((term) => term.trim())
      .filter((term) => term != "");
    if (terms.length == 0) {
      return allItems;
    }
    return allItems.filter((item) => {
      const name = item.name.toLowerCase();
      return terms.some((term) => name.includes(term));
    });
  }, [items, query]);

  const [visibleCount, setVisibleCount] = React.useState(INITIAL_VISIBLE_COUNT);

  // Start over whenever the candidate list changes (new items or new filter text) or the dropdown
  // is opened or closed.
  React.useEffect(() => {
    setVisibleCount(INITIAL_VISIBLE_COUNT);
  }, [filteredItems, isOpen]);

  const visibleItems = React.useMemo(
    () => (filteredItems.length <= visibleCount ? filteredItems : filteredItems.slice(0, visibleCount)),
    [filteredItems, visibleCount]
  );

  const loadMore = React.useCallback(() => {
    setVisibleCount((count) => (count >= filteredItems.length ? count : count + LOAD_MORE_COUNT));
  }, [filteredItems.length]);

  const onScroll = React.useCallback(
    (event: React.UIEvent) => {
      const listbox = event.currentTarget;
      if (listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - SCROLL_THRESHOLD_PX) {
        loadMore();
      }
    },
    [loadMore]
  );

  // Keyboard navigation moves the highlight without necessarily firing a scroll event, so the last
  // few rendered options double as the trigger for the next page.
  const tailIds = React.useMemo(() => {
    const ids = new Set<string>();
    for (let i = Math.max(0, visibleItems.length - KEYBOARD_LOOKAHEAD); i < visibleItems.length; i++) {
      ids.add(visibleItems[i].id);
    }
    return ids;
  }, [visibleItems]);

  const onActiveOptionChange = React.useCallback(
    (_event: any, data: any) => {
      const activeValue = data?.nextOption?.value;
      if (activeValue && tailIds.has(activeValue)) {
        loadMore();
      }
    },
    [tailIds, loadMore]
  );

  return {
    visibleItems,
    filteredItems,
    listboxProps: { onScroll },
    onActiveOptionChange,
    disableAutoFocus: (items?.length ?? 0) > INITIAL_VISIBLE_COUNT,
  };
};
